import { Fragment, useId, useMemo, type JSX } from "react";
import clsx from "clsx";
import { Listbox, Transition } from "@headlessui/react";
import cn from "classnames";

export interface ISelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  description?: string;
  status?: "not started" | "in progress" | "completed";
}

interface SelectProps {
  label?: string;
  options: ISelectOption[];
  value: string | null;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export function Select(props: SelectProps): JSX.Element {
  const { label, options, value, onChange, placeholder = "Select…", className, disabled } = props;
  const listId = useId();
  const selected = useMemo(() => options.find((o) => o.value === value) ?? null, [options, value]);

  return (
    <div className={clsx("text-eerie-black font-family-third relative w-full font-bold", className)}>
      <Listbox value={value} onChange={onChange} disabled={disabled}>
        {({ open }) => (
          <>
            <Listbox.Button
              aria-controls={listId}
              className={clsx(
                "flex w-full items-center justify-between rounded-lg border border-transparent",
                "bg-background-grey px-6 py-5 text-left transition-all duration-150",
                "hover:bg-[#EEEEEE] active:bg-[#E9E9E9]",
                "focus-visible:outline-none",
                disabled && "cursor-not-allowed opacity-55",
              )}
            >
              <span className={clsx("text-base font-bold", !selected && "text-eerie-black")}>
                {selected ? selected.label : placeholder}
              </span>
              <ChevronDown
                className={clsx(
                  "h-8 w-8 text-current transition-transform duration-300 ease-out",
                  open && "-rotate-180",
                )}
              />
            </Listbox.Button>
            <Transition
              as={Fragment}
              enter="transition transform duration-100 ease-out"
              enterFrom="opacity-0 scale-[0.98]"
              enterTo="opacity-100 scale-100"
              leave="transition transform duration-75 ease-in"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-[0.98]"
            >
              <Listbox.Options
                id={listId}
                className={cn(
                  "absolute top-[calc(100%+8px)] right-0 left-0 z-[1000]",
                  "bg-background-grey rounded-lg px-2 py-3",
                  "outline-none",
                  "shadow-[0_4px_6px_-1px_rgba(0,0,0,0.10),0_2px_4px_-2px_rgba(0,0,0,0.10)]",
                )}
              >
                {label && <div className="px-3 pt-2 pb-3 text-base font-bold text-[#8b8b8b]">{label}</div>}
                <ul role="presentation" className="flex max-h-80 w-full list-none flex-col gap-1 overflow-auto p-0">
                  {options.map((opt) => (
                    <Listbox.Option key={opt.value} value={opt.value} disabled={opt.disabled} as={Fragment}>
                      {({ active, selected, disabled }) => (
                        <li
                          className={cn(
                            "flex items-center justify-between gap-2 rounded-lg px-4 py-3",
                            "cursor-pointer text-base leading-6 font-bold transition-all duration-150",
                            active && "bg-[#f8f8f8]",
                            selected && "bg-[#f6f6f6]",
                            disabled && "cursor-not-allowed opacity-50",
                          )}
                        >
                          <p className="pointer-events-none">{opt.label}</p>
                          {selected && <CheckIcon className="text-eerie-black h-4 w-4" />}
                        </li>
                      )}
                    </Listbox.Option>
                  ))}
                </ul>
              </Listbox.Options>
            </Transition>
          </>
        )}
      </Listbox>
    </div>
  );
}

function ChevronDown(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none" {...props}>
      <path d="M8 12L16 20L24 12" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CheckIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 16 16" fill="none" {...props}>
      <path
        d="M13.3333 4L5.99999 11.3333L2.66666 8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
